import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CalculationOperation, CalculationStateService } from '../../../optimistic-planning/calculation-state.service';

/**
 * Wide bottom drawer panel showing detailed information about a planning operation.
 * Replaces/overlays the details dock when a calc-slot-indicator dot is clicked.
 *
 * Spec: CALC-PANEL-V2-SPEC.md
 */
@Component({
  selector: 'calc-detail-panel',
  templateUrl: './calc-detail-panel.component.html',
  styleUrls: ['./calc-detail-panel.component.scss']
})
export class CalcDetailPanelComponent implements OnChanges, OnDestroy {
  @Input() operation: CalculationOperation | null = null;
  @Input() previousOps: CalculationOperation[] = [];
  @Input() pinned: boolean = false;

  @Output() close = new EventEmitter<void>();
  @Output() pin = new EventEmitter<void>();
  @Output() verify = new EventEmitter<string>();
  @Output() retry = new EventEmitter<CalculationOperation>();
  @Output() dismiss = new EventEmitter<string>();

  now: number = Date.now();
  private tickSub: Subscription;

  constructor(private calcState: CalculationStateService, private t: TranslateService) {}

  tr(key: string, params?: object): string {
    return this.t.instant('OPTIMISTIC.' + key, params);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operation']) {
      this.syncTimer();
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private syncTimer(): void {
    if (this.operation?.status === 'Calculating' && !this.tickSub) {
      this.tickSub = interval(1000).subscribe(() => this.now = Date.now());
    } else if (this.operation?.status !== 'Calculating') {
      this.stopTimer();
      this.now = Date.now();
    }
  }

  private stopTimer(): void {
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }

  // ── Status helpers ──

  get isCalculating(): boolean { return this.operation?.status === 'Calculating'; }
  get isCompleted(): boolean { return this.operation?.status === 'Completed'; }
  get isFailed(): boolean { return this.operation?.status === 'Failed'; }

  get statusLabel(): string {
    if (this.isCalculating) return this.tr('STATUS_PLANNING');
    if (this.isCompleted) return this.tr('STATUS_PLANNED');
    if (this.isFailed) return this.tr('STATUS_FAILED');
    return '';
  }

  get statusIcon(): string {
    if (this.isCalculating) return 'fa-spinner fa-spin';
    if (this.isCompleted) return 'fa-check';
    if (this.isFailed) return 'fa-times';
    return '';
  }

  // ── Operation type label (user-friendly) ──

  get operationTypeLabel(): string {
    if (!this.operation) return '';
    const map: Record<string, string> = {
      plan: 'TYPE_PLAN', move: 'TYPE_MOVE', assign: 'TYPE_ASSIGN',
      sendPb: 'TYPE_SEND_PB', cancelPb: 'TYPE_CANCEL_PB', unplanPb: 'TYPE_UNPLAN_PB',
      cancelAndUnplanPb: 'TYPE_CANCEL_AND_UNPLAN_PB', replanPb: 'TYPE_REPLAN_PB',
      cancelAndReplanPb: 'TYPE_CANCEL_AND_REPLAN_PB',
      sendTo: 'TYPE_SEND_TO', cancelTo: 'TYPE_CANCEL_TO', unplanTo: 'TYPE_UNPLAN_TO',
      cancelAndUnplanTo: 'TYPE_CANCEL_AND_UNPLAN_TO', resendTo: 'TYPE_RESEND_TO',
      sendNextPb: 'TYPE_SEND_NEXT_PB', removeCapacity: 'TYPE_REMOVE_CAPACITY'
    };
    return this.tr(map[this.operation.operationType] || 'TYPE_DEFAULT');
  }

  // ── Truck/driver line ──

  get truckDriverLine(): string {
    const parts: string[] = [];
    if (this.operation?.driverName) parts.push(this.operation.driverName);
    if (this.operation?.truckName) parts.push(this.operation.truckName);
    return parts.join(' · ');
  }

  get subtitleLine(): string {
    const parts: string[] = [];
    parts.push(this.truckDriverLine);
    parts.push(this.operationTypeLabel);
    if (this.operation?.planDate) parts.push(this.operation.planDate);
    return parts.filter(p => p).join(' · ');
  }

  // ── Timing ──

  get elapsedSeconds(): number {
    if (!this.operation) return 0;
    return Math.floor((this.now - new Date(this.operation.startedAt).getTime()) / 1000);
  }

  get durationDisplay(): string {
    if (!this.operation?.durationMs) return '';
    return (this.operation.durationMs / 1000).toFixed(1) + 's';
  }

  get timingLabel(): string {
    if (this.isCalculating) return this.elapsedSeconds + 's ' + this.tr('ELAPSED');
    if (this.isCompleted || this.isFailed) return this.tr('COMPLETED_IN') + ' ' + this.durationDisplay;
    return '';
  }

  get autoDismissMinutes(): number {
    if (!this.operation?.expiresAt) return 0;
    const remaining = new Date(this.operation.expiresAt).getTime() - this.now;
    return Math.max(0, Math.ceil(remaining / 60000));
  }

  // ── Time formatting ──

  formatTime(isoString: string): string {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  // ── PB details ──

  get planningBlockDetails() {
    return this.operation?.planningBlockDetails || [];
  }

  // ── Results ──

  get pbOutcomes() {
    return this.operation?.result?.planningBlockOutcomes || [];
  }

  get conflicts() {
    const all = this.operation?.result?.conflicts || [];
    const seen = new Set<string>();
    return all.filter(c => {
      const key = `${(c.description || '').trim()}|${(c.resource || '').trim()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  get warnings() {
    return this.operation?.result?.warnings || [];
  }

  get hasAlerts(): boolean {
    return this.conflicts.length > 0 || this.warnings.length > 0;
  }

  /** Warnings that aren't already represented by the conflicts list (e.g. date changes, rejections) */
  get nonConflictWarnings(): string[] {
    // The warnings array from generateWarnings now includes conflict descriptions inline,
    // so filter out any that match a conflict description to avoid duplication
    const conflictDescs = new Set(this.conflicts.map(c => c.description));
    return this.warnings.filter(w => !conflictDescs.has(w));
  }

  get errorMessage(): string {
    return this.operation?.result?.errorMessage || '';
  }

  get createdToId(): string {
    return this.operation?.result?.createdTransportOrderId || '';
  }

  // ── Actions ──

  onClose(): void {
    this.close.emit();
  }

  onPin(): void {
    this.pin.emit();
  }

  onVerify(): void {
    if (this.operation) {
      this.verify.emit(this.operation.operationId);
    }
  }

  onRetry(): void {
    if (this.operation) {
      this.retry.emit(this.operation);
    }
  }

  copyError(event: Event): void {
    event.stopPropagation();
    const msg = this.errorMessage || '';
    if (msg && navigator.clipboard) {
      navigator.clipboard.writeText(msg);
    }
  }

  onDismiss(): void {
    if (this.operation) {
      this.dismiss.emit(this.operation.operationId);
    }
  }

  // ── History helpers ──

  getHistoryLabel(op: CalculationOperation): string {
    const time = this.formatTime(op.startedAt);
    const type = this.getOperationTypeLabel(op);
    return `${time} · ${type}`;
  }

  getHistoryMeta(op: CalculationOperation): string {
    const duration = op.durationMs ? (op.durationMs / 1000).toFixed(1) + 's' : '?';
    const verified = op.verified ? ' · ' + this.tr('VERIFIED') : '';
    return `${this.tr('COMPLETED_IN')} ${duration}${verified}`;
  }

  getOperationTypeLabel(op: CalculationOperation): string {
    const map: Record<string, string> = {
      plan: 'TYPE_PLAN', move: 'TYPE_MOVE', assign: 'TYPE_ASSIGN',
      sendPb: 'TYPE_SEND_PB', cancelPb: 'TYPE_CANCEL_PB', unplanPb: 'TYPE_UNPLAN_PB',
      cancelAndUnplanPb: 'TYPE_CANCEL_AND_UNPLAN_PB', replanPb: 'TYPE_REPLAN_PB',
      cancelAndReplanPb: 'TYPE_CANCEL_AND_REPLAN_PB',
      sendTo: 'TYPE_SEND_TO', cancelTo: 'TYPE_CANCEL_TO', unplanTo: 'TYPE_UNPLAN_TO',
      cancelAndUnplanTo: 'TYPE_CANCEL_AND_UNPLAN_TO', resendTo: 'TYPE_RESEND_TO',
      sendNextPb: 'TYPE_SEND_NEXT_PB', removeCapacity: 'TYPE_REMOVE_CAPACITY'
    };
    return this.tr(map[op.operationType] || 'TYPE_DEFAULT');
  }
}
